import { ReactNode } from 'react';

export type Currency = 'USD' | 'ARS' | 'GS' | 'EUR' | '$' | 'Gs';

export interface NumberDisplayI {
    value?: string | number;
    unit?: string;
    currency?: Currency;
    round?: boolean;
    children?: (value: string, unit?: string, currency?: string) => ReactNode;
    className?: string;
}

export function getCurrencySymbol(currency: Currency) {
    switch (currency) {
        case 'EUR':
            return '€';
        case 'USD':
            return 'USD';
        case 'ARS':
            return 'ARS ';
        case 'GS':
            return 'Gs';
        case 'Gs':
            return 'GS';
        case '$':
            return '$';
        default:
            return '';
    }
}

/**
 * Receives data that needs to be displayed as localeString.
 * If the given data is undefined or is not a number or number string, it'll return an empty react fragment.
 *
 * @param props.unit - A unit can be specified, for example 'kg'
 * @param props.currency - A currency can be specified, for example 'ars'
 * @param props.round - It rounds the number when this param is true
 *
 * */
export const NumberDisplay = ({ value, unit, round, currency, children, className }: Readonly<NumberDisplayI>) => {
    let parsedValue = value !== undefined ? +value : undefined;

    if (parsedValue === undefined || isNaN(parsedValue)) {
        return <></>;
    }

    if (round) {
        parsedValue = Math.round(parsedValue);
    }

    const formattedValue = parsedValue.toLocaleString('es-AR');

    if (children) {
        return <>{children(formattedValue, unit, currency && getCurrencySymbol(currency))}</>;
    }

    if (currency) {
        return <CurrencyDisplay value={parsedValue} currency={currency} className={className} />;
    }

    return <span className={className}>{`${formattedValue}${unit || ''}`}</span>;
};

export function CurrencyDisplay(props: Readonly<{ value: number; currency: Currency; className?: string }>) {
    const sign = props.value < 0 ? '- ' : '';
    const symbol = getCurrencySymbol(props.currency);
    const number = Math.abs(props.value).toLocaleString('es-AR');

    return <span className={props.className}>{`${sign}${symbol} ${number}`}</span>;
}
